import {
  BoxLayout,
  DialogLayout,
  NewConvergeButton,
} from '../../../../presentation';
import { FieldErrors, FieldValues, useFormContext } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import {
  useRaterActionDispatchContext,
  useRaterStateContextSelector,
  useRaterUnsavedCounts,
} from '../data';

import { RaterMode } from '@converge/graphql';
import { UnderwritingSubmissionDetailPageSlug } from '@converge/data';
import config from '@converge/config';
import { formatList } from '@converge/utils';
import pluralize from 'pluralize';
import { underwritingQuoteToUpsertVariables } from '../data/rater.upsert-quote-variables.create';
import { useConvergeSnackBar } from '../../../../contexts/use-snackbar';
import { useUnderwritingSubmissionDetailPage } from '../../underwriting.submission.detail.hooks';
import { useUpsertQuoteMutation } from '@converge/graphql';
import {
  SR_MAX_ITEM_PERCENTAGE,
  SR_MIN_ITEM_PERCENTAGE,
} from '../data/rater.constants';

interface IRaterQuoteSaveButtonProps {
  onComplete?: () => void;
}

export const RaterQuoteSaveButton = ({
  onComplete,
}: IRaterQuoteSaveButtonProps) => {
  const [showValidationErrorModal, setShowValidationErrorModal] =
    useState<boolean>(false);
  const {
    hasQuoteChanged,
    asTextWithoutZeros,
    total: totalUnsavedChanges,
  } = useRaterUnsavedCounts();
  const { notify, closeSnackbar } = useConvergeSnackBar();
  const { navigate } = useUnderwritingSubmissionDetailPage();
  const { upsertQuote, loading, error } = useUpsertQuoteMutation();

  const {
    isEditable,
    quote,
    submission,
    mode,
    userQuotes,
    isRequestingRatingUpdate,
    reasonQuoteHasChanged,
    isRequestingUpsertQuoteNow,
    changedFields,
    isInvalidData,
    hasTrackingConflict,
  } = useRaterStateContextSelector(
    ({
      isRequestingUpsertQuoteNow,
      isRequestingUpsertQuoteAfterRating,
      isEditable,
      quote,
      submission,
      mode,
      userQuotes,
      isRequestingRatingUpdate,
      changedFields,
      isInvalidData,
      hasTrackingConflict,
      inForcePolicy,
    }) => {
      return {
        changedFields,
        isRequestingUpsertQuoteNow,
        isRequestingUpsertQuote:
          isRequestingUpsertQuoteNow || isRequestingUpsertQuoteAfterRating,
        isEditable,
        quote,
        submission,
        mode,
        userQuotes,
        isRequestingRatingUpdate,
        reasonQuoteHasChanged: quote.changeReason,
        isInvalidData,
        hasTrackingConflict,
        inForcePolicy,
      };
    }
  );

  const { actions, dispatch } = useRaterActionDispatchContext();

  const minItemDecimal =
    (quote.srMinItemPercentage ?? SR_MIN_ITEM_PERCENTAGE) / 100;
  const maxItemDecimal =
    (quote.srMaxItemPercentage ?? SR_MAX_ITEM_PERCENTAGE) / 100;
  // Guards against server-returned out-of-range values bypassing the UI clamp.
  // User-entered values are always clamped before storage, so this primarily
  // catches legacy quotes loaded with percentages outside the current bounds.
  const hasInvalidScheduleRating = quote.scheduleRatings.some(
    (r) =>
      r.quote.percentage != null &&
      (r.quote.percentage < minItemDecimal ||
        r.quote.percentage > maxItemDecimal)
  );

  let text = 'Save';

  switch (mode) {
    case RaterMode.CreateQuote:
      text = 'Create quote option';
      break;
    case RaterMode.EditQuote:
      text = 'Save quote';
      if (hasQuoteChanged || totalUnsavedChanges > 0) {
        text += `*`;
      }
      break;
    default:
      text = 'Save';
  }

  if (!isEditable || quote.isObsolete) {
    text = 'Close';
  }

  const {
    formState: { errors },
  } = useFormContext();

  useEffect(() => {
    dispatch(
      actions.setUpsertQuoteState({
        loading,
        error,
      })
    );
  }, [actions, dispatch, error, loading]);

  const runUpsertQuote = useCallback(async () => {
    const notification =
      !config.featureFlags.enableRaterStateIndicator &&
      notify(
        mode === RaterMode.CreateQuote
          ? 'Creating quote...'
          : 'Saving quote...',
        {
          variant: 'info',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        }
      );

    try {
      const data = await upsertQuote({
        variables: underwritingQuoteToUpsertVariables({
          mode,
          quote,
          submissionUuid: submission.uuid,
          totalUserQuotes: userQuotes.pageInfo.totalResults,
        }),
      });
      dispatch(actions.updateAfterQuoteUpsert());

      if (mode === RaterMode.CreateQuote) {
        const navigateToQuote = true;
        if (navigateToQuote) {
          navigate.page({
            sectionUrl: `quote`,
          });
        } else {
          if (!data.data?.upsertQuote) {
            throw new Error(
              `Quote was not created, and the API did not return an error.`
            );
          }
          navigate.page({
            sectionUrl: `${UnderwritingSubmissionDetailPageSlug.Quote}/${RaterMode.EditQuote}/${data.data?.upsertQuote.uuid}`,
          });
        }
      }
      if (onComplete) {
        onComplete();
      }

      !config.featureFlags.enableRaterStateIndicator &&
        notify(
          mode === RaterMode.CreateQuote ? 'Quote created.' : 'Quote saved.',
          {
            variant: 'success',
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
          }
        );
    } catch (e) {
      notify(
        `Error saving quote rating: ${
          e instanceof Error ? e.message : 'unknown error'
        }`.substring(0, 500),
        {
          variant: 'error',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        }
      );
    } finally {
      if (notification) {
        closeSnackbar(notification);
      }
    }
  }, [
    notify,
    mode,
    upsertQuote,
    quote,
    submission.uuid,
    userQuotes.pageInfo.totalResults,
    dispatch,
    actions,
    onComplete,
    navigate,
    closeSnackbar,
  ]);

  useEffect(() => {
    if (!isRequestingRatingUpdate && isRequestingUpsertQuoteNow && !loading) {
      runUpsertQuote();
    }
  }, [
    isRequestingRatingUpdate,
    isRequestingUpsertQuoteNow,
    loading,
    runUpsertQuote,
  ]);

  return (
    <>
      <DialogLayout
        open={showValidationErrorModal}
        showCloseButton
        onCloseIconClick={() => setShowValidationErrorModal(false)}
        onClose={() => setShowValidationErrorModal(false)}
        maxWidth="lg"
        header={'Invalid quote option'}
        bodyText="Please fix these issues before saving:"
        body={
          <>
            <ul>
              <ErrorList errors={errors} type={'subLimit'} />
              <ErrorList errors={errors} type={'subRetention'} />
              <ErrorList errors={errors} type={'selectedFusionScoreModifier'} />
            </ul>
          </>
        }
        footer={
          <BoxLayout sx={{ m: 2 }}>
            <NewConvergeButton
              type="reset"
              onClick={() => setShowValidationErrorModal(false)}
              label="Okay"
            />
          </BoxLayout>
        }
      />
      <NewConvergeButton
        loading={loading || isRequestingRatingUpdate}
        color="primary"
        disabled={
          isRequestingRatingUpdate ||
          isInvalidData ||
          hasInvalidScheduleRating ||
          hasTrackingConflict ||
          quote.inForcePolicy
        }
        variant="contained"
        tooltip={`Save ${
          asTextWithoutZeros
            ? asTextWithoutZeros
            : totalUnsavedChanges
            ? pluralize('change', totalUnsavedChanges)
            : hasQuoteChanged
            ? 'quote'
            : ''
        }${
          config.isNodeEnvDevelopment &&
          (reasonQuoteHasChanged || changedFields.length > 0)
            ? ` (${[
                reasonQuoteHasChanged,
                changedFields.length > 0
                  ? formatList(changedFields)
                  : undefined,
              ].filter(Boolean)})`
            : ''
        }`}
        onClick={() => {
          if (!isEditable || quote.isObsolete) {
            navigate.page({
              sectionUrl: UnderwritingSubmissionDetailPageSlug.Quote,
            });
            return;
          }

          if (
            errors
              ? Object.keys({
                  ...errors['selectedFusionScoreModifier'],
                  ...errors['subLimit'],
                  ...errors['subRetention'],
                }).length > 0
              : false
          ) {
            setShowValidationErrorModal(true);
            return;
          }

          runUpsertQuote();
        }}
        label={text}
      />
    </>
  );
};

const ErrorList = <T extends FieldValues = FieldValues>({
  errors,
  type,
}: {
  errors?: FieldErrors<T>;
  type: keyof FieldErrors<T>;
}) => {
  const errorsToShow = errors?.[type];

  if (errorsToShow) {
    return (
      <>
        {Object.keys(errorsToShow).map((key) => {
          return (
            <li key={key}>
              {key}:{' '}
              {
                (errorsToShow as Record<string, { message: string }>)[key]
                  ?.message
              }
            </li>
          );
        })}
      </>
    );
  }

  return <></>;
};
